import React from "react";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";

import Avatar from "../avatar/Avatar";
import ToolTip from "../tooltip/ToolTip";

/**
 * オファー作成ウィザードの「送信先ユーザーカード」の見た目（白銀比カード +
 * Avatar + 表示名 / タイトル / 所属）を共通部品として切り出したもの。
 *
 * - 送信側（`RecipientStep` の coverflow / arc カード）
 * - 受領側オンボーディングのプロフィールプレビュー
 * の双方で同一デザインを使うため、ここに一元化する。
 */

// ===== カード寸法 / アバターサイズ定数 =====

/**
 * 送信先ユーザーカードの基準寸法（白銀比 1:√2 ≈ 1:1.414）。
 * ロールカード（`PortraitCard`、{@link ./RolePicker}）と同サイズに揃え、
 * オファー作成ウィザード上で「ユーザーカード ↔ ロールカード」の対関係をつくる。
 */
export const CARD_WIDTH_PX = 256;
export const CARD_HEIGHT_PX = 362;

/** Coverflow（既定）モードでの Avatar サイズ。 */
export const COVERFLOW_AVATAR_SIZE_PX = 96;
/** arc（マガジン風）モードで大きめに見せる Avatar サイズ。 */
export const ARC_AVATAR_SIZE_PX = 112;

// ===== Styled Components =====

export const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

/**
 * カード実寸に対する等比スケール係数。基準はポップオーバー表示と同じ
 * {@link CARD_WIDTH_PX}（256px）。カードは常に白銀比なので幅比 = 高さ比。
 */
export const cardScaleFor = (widthPx: number): number =>
  widthPx / CARD_WIDTH_PX;

/**
 * ホイール（arc モード）のカード中身を「基準寸法（256×362 = ポップオーバーの
 * カードと同一）で描いてから、カード実寸へ丸ごと `transform: scale()`」する
 * ためのキャンバス。
 *
 * ホイールのカード枠はビューポートの空き領域にフィットして伸縮する
 * （{@link ./useOfferWheelLayout}）が、中身のフォント / アバター / 余白を
 * px 固定のまま枠だけ伸縮させると縮小時・拡大時にバランスが崩れる。
 * 基準寸法で組んだレイアウトを等比スケールすることで、どのカードサイズでも
 * ポップオーバーと同じ見た目バランスを維持する。
 *
 * 親（`RadioCardGroup` の Slot 等、カード実寸を持つ要素）の左上を原点に、
 * scale 後の外形が親とぴったり一致する。
 */
export const CardScaleCanvas = styled.div<{ $scale: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${CARD_WIDTH_PX}px;
  height: ${CARD_HEIGHT_PX}px;
  transform: scale(${(p) => p.$scale});
  transform-origin: top left;
`;

/*
 * ステップ2のロール選択カード（`RadioCard`）と同じ選択中演出に揃える:
 *  - 選択時: border + outline のダブルライン（var(--soremo-blue)）
 *  - hover : 青い soft glow（box-shadow）
 * カード内背景は CreditCard 由来の暗めグラデーションをやめ、もう一段明るくする。
 */
export const OfferUserCardFrame = styled.button<{
  $selected: boolean;
  $variant?: "coverflow" | "arc";
}>`
  appearance: none;
  width: 100%;
  height: 100%;
  cursor: pointer;
  padding: 24px 12px 16px;
  border-radius: 12px;
  border: 1px solid
    ${(p) => (p.$selected ? "var(--soremo-blue)" : "var(--soremo-border)")};
  outline: 1px solid
    ${(p) => (p.$selected ? "var(--soremo-blue)" : "var(--soremo-border)")};
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  box-shadow: 2px 4px 8px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  transition:
    border-color 0.15s,
    outline-color 0.15s,
    box-shadow 0.15s;

  /*
   * arc（マガジン風）モードのレイアウト微調整。coverflow（既定）には適用しない。
   *  - padding-top を 32px に広げ、カード上端 → Avatar の余白を確保。
   *  - Avatar（先頭子要素）下に 20px の margin を足し、gap 12px と合算で
   *    Avatar → 表示名間を 32px に広げる（表示名 / タイトル / 所属の各間隔は
   *    gap 12px のまま維持される）。
   */
  ${(p) =>
    p.$variant === "arc"
      ? `
      padding-top: 32px;
      & > :first-child {
        margin-bottom: 20px;
      }
    `
      : ""}

  @media (hover: hover) {
    &:hover {
      border: 1px solid
        ${(p) =>
          p.$selected ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
      outline: ${(p) =>
        p.$selected ? "1px solid var(--soremo-blue)" : "none"};
      box-shadow: 0 0 6px 0
        ${(p) =>
          p.$selected ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
    }
  }
`;

export const CardLabel = styled.div`
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * カード左辺に「左下 → 左上」方向で描く英語表示名のマガジン風タイポ。
 *
 * ロールカード右辺の `RotatedCreditTitle`（[RolePicker.tsx](./RolePicker.tsx)）と
 * 対になる左辺ラベル。`writing-mode: vertical-rl` + `transform: rotate(180deg)` の
 * 組み合わせで、字面は左下を起点に左上へ向かって流れる（`sideways-lr` の
 * ブラウザ差異を避けるための互換実装）。
 */
export const RotatedDisplayNameEn = styled.div`
  position: absolute;
  left: 12px;
  top: 12px;
  bottom: 12px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-orientation: mixed;
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-style: normal;
  font-size: 24px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffffff;
  mix-blend-mode: difference;
  opacity: 0.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

// ===== カード本体（Avatar + 表示名 / タイトル / 所属） =====

export interface OfferUserCardBodyProps {
  displayName: string;
  title?: string;
  affiliation?: string;
  avatarUrl?: string | null;
  avatarSize: number;
  /** 選択中のときだけ公開プロフィール QR を表示する（任意）。 */
  selected?: boolean;
  /** 公開プロフィール URL。指定かつ selected のとき QR を表示する。 */
  profileUrl?: string;
}

/**
 * `OfferUserCardFrame` の内側に並ぶ「Avatar + 表示名 + タイトル + 所属」の縦積み本体。
 * coverflow / arc / プレビューで完全に同形のため共通部品として切り出す。
 * Avatar サイズだけ呼び出し側で差し替える。
 */
export const OfferUserCardBody: React.FC<OfferUserCardBodyProps> = ({
  displayName,
  title = "",
  affiliation = "",
  avatarUrl,
  avatarSize,
  selected = false,
  profileUrl = "",
}) => (
  <>
    <Avatar size={avatarSize} src={avatarUrl ?? undefined} name={displayName} />
    <CardLabel className="heading-spacing-18">{displayName}</CardLabel>
    {title && (
      <CardLabel className="bodytext-13 u-line-height-200">{title}</CardLabel>
    )}
    {affiliation && (
      <CardLabel className="bodytext-11 u-text-light-gray u-line-height-100">
        {affiliation}
      </CardLabel>
    )}
    {selected && profileUrl && (
      <ToolTip description="QR を読み取る / クリックで公開プロフィールを開く">
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="公開プロフィールを開く"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "inline-flex",
            marginTop: 8,
            padding: 4,
            background: "#fff",
            borderRadius: 4,
            border: "1px solid var(--soremo-border)",
          }}
        >
          <QRCodeSVG value={profileUrl} size={56} />
        </a>
      </ToolTip>
    )}
  </>
);
